import { makeAutoObservable } from 'mobx';
import { CreateOptionCategoryData, UpdateOptionCategoryData, OptionCategoryModel } from '../../../model/professional/option-category-model';
import toastStore from '../../../store/toast-store';
import categoryStore from '../../../store/category-store';
import { OptionCategory } from '../../../model/professional/option-category-model';

/**
 * ViewModel pour gérer le formulaire de catégorie d'options
 */
class CategoryFormViewModel {
  isSubmitting: boolean = false;
  error: string | null = null;
  private optionCategoryModel: OptionCategoryModel;
  name: string = '';
  description: string = '';
  serviceId: string = '';
  isRequired: boolean = false;
  isMultipleChoice: boolean = false;

  setName(name: string): void {
    this.name = name;
  }

  setDescription(description: string): void {
    this.description = description;
  }

  setServiceId(serviceId: string): void {
    this.serviceId = serviceId;
  }

  setIsRequired(isRequired: boolean): void {
    this.isRequired = isRequired;
  }

  setIsMultipleChoice(isMultipleChoice: boolean): void {
    this.isMultipleChoice = isMultipleChoice;
  }

  setError(error: string | null): void {
    this.error = error;
  }

  constructor() {
    makeAutoObservable(this);
    this.optionCategoryModel = OptionCategoryModel.getInstance();
  }

  initializeForm(category: OptionCategory | null): void {
    if (category) {
      this.setName(category.name || '');
      this.setDescription(category.description || '');
      this.setServiceId(category.serviceId || '');
      this.setIsRequired(category.isRequired ?? false);
      this.setIsMultipleChoice(category.isMultipleChoice ?? false);
      this.setError(null);
    } else {
      this.resetForm();
    }
  }

  /**
   * Réinitialise l'état du formulaire
   */
  resetForm(): void {
    this.isSubmitting = false;
    this.error = null;
    this.name = '';
    this.description = '';
    this.serviceId = '';
    this.isRequired = false;
    this.isMultipleChoice = false;
  }

  /**
   * Définit l'état de chargement
   */
  private setLoading(value: boolean): void {
    this.isSubmitting = value;
    if (!value) return;
    this.error = null;
  }

  /**
   * Gère les erreurs
   */
  private handleError(message: string): void {
    console.error(message);
    this.error = message;
    toastStore.error(message);
  }

  /**
   * Valide les champs obligatoires pour une catégorie
   * @param data Données de la catégorie
   * @returns true si tous les champs obligatoires sont remplis, false sinon
   */
  validateCategoryData(data: CreateOptionCategoryData | UpdateOptionCategoryData): boolean {
    if (!data.name || data.name.trim() === '') {
      this.error = 'Le nom de la catégorie est obligatoire';
      toastStore.error('Le nom de la catégorie est obligatoire');
      return false;
    }
    
    return true;
  }

  /**
   * Soumet le formulaire de création de catégorie
   * @param data Données du formulaire
   * @returns ID de la catégorie créée ou null en cas d'erreur
   */
  async submitCreateForm(data: CreateOptionCategoryData): Promise<string | null> {
    try {
      this.setLoading(true);
      
      if (!this.validateCategoryData(data)) {
        this.setLoading(false);
        return null;
      }
      
      const response = await this.optionCategoryModel.createCategory(data);
      
      if (!response?.success || !response.data) {
        throw new Error(response?.message || 'Erreur lors de la création de la catégorie');
      }
      
      categoryStore.addCategory(response.data);
      toastStore.success(response.message || 'Catégorie créée avec succès');
      
      return response.data.id;
    } catch (error: any) {
      this.handleError(error.message || 'Erreur lors de la création de la catégorie');
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Soumet le formulaire de mise à jour de catégorie
   * @param categoryId ID de la catégorie à mettre à jour
   * @param data Données du formulaire
   * @param currentIsActive État actuel d'activation
   * @param newIsActive Nouvel état d'activation
   * @returns true si la mise à jour a réussi
   */
  async submitUpdateForm(
    categoryId: string, 
    data: UpdateOptionCategoryData,
    currentIsActive: boolean,
    newIsActive: boolean
  ): Promise<boolean> {
    try {
      this.setLoading(true);
      
      if (!this.validateCategoryData(data)) {
        this.setLoading(false);
        return false;
      }
      
      const updateResponse = await this.optionCategoryModel.updateCategory(categoryId, data);
      
      if (!updateResponse?.success || !updateResponse.data) {
        throw new Error(updateResponse?.message || 'Erreur lors de la mise à jour de la catégorie');
      }
      
      categoryStore.updateCategoryInStore(categoryId, updateResponse.data);
      
      if (currentIsActive !== newIsActive) {
        const statusResponse = await this.optionCategoryModel.toggleCategoryStatus(categoryId, newIsActive);
        
        if (!statusResponse?.success || !statusResponse.data) {
          throw new Error(statusResponse?.message || 'Erreur lors de la mise à jour du statut');
        }
        
        categoryStore.updateCategoryInStore(categoryId, statusResponse.data);
      }
      
      toastStore.success('Catégorie mise à jour avec succès');
      return true;
    } catch (error: any) {
      this.handleError(error.message || 'Erreur lors de la mise à jour de la catégorie');
      return false;
    } finally {
      this.setLoading(false);
    }
  }
}

const categoryFormViewModel = new CategoryFormViewModel();

// Hook pour accéder au ViewModel
export const useCategoryFormViewModel = () => {
  return categoryFormViewModel;
};

export default categoryFormViewModel;
